import { area } from "@turf/area";
import { featureCollection, multiPolygon, polygon } from "@turf/helpers";
import { intersect } from "@turf/intersect";
import { kinks } from "@turf/kinks";
import type { z } from "zod";
import type { MapVertex, PolygonRings } from "../../utils/area.js";
import { pointInPolygon } from "../../utils/pointInPolygon.js";
import type { Site } from "./site.js";

export const containsPoint = (site: Site, lat: number, long: number): boolean => {
    if (site.polygons && site.polygons[0]) {
        const transformedBoundaries = site.polygons[0].map((p) => [p.lat, p.lng]);
        const point = [lat, long];
        return pointInPolygon(point, transformedBoundaries);
    }
    return false;
};

const toClosedRing = (vertices: readonly MapVertex[]): number[][] | undefined => {
    if (!vertices.every((vertex) => Number.isFinite(vertex.lat) && Number.isFinite(vertex.lng))) return undefined;
    const ring = vertices.map((vertex) => [vertex.lng as number, vertex.lat as number]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first && (first[0] !== last[0] || first[1] !== last[1])) ring.push([...first]);
    return ring.length >= 4 ? ring : undefined;
};

const toMultiPolygon = (rings: number[][][]) => (rings.length ? multiPolygon(rings.map((ring) => [ring])) : undefined);

export const isValidPolygonRing = (vertices: readonly MapVertex[]): boolean => {
    // kinks reports a repeated consecutive vertex as a self-intersection.
    const deduplicated = vertices.filter(
        (vertex, index) =>
            index === 0 || vertex.lat !== vertices[index - 1].lat || vertex.lng !== vertices[index - 1].lng,
    );
    const ring = toClosedRing(deduplicated);
    if (!ring || new Set(ring.map(([lng, lat]) => `${lng},${lat}`)).size < 3) return false;
    try {
        const closed = polygon([ring]);
        // area() alone reads collinear lat/lng points as a sliver; measure the shape the overlap check sees.
        const normalized = intersect(featureCollection([closed, closed]));
        return normalized !== null && area(normalized) > 0 && kinks(closed).features.length === 0;
    } catch {
        return false;
    }
};

export const addPolygonRingIssues = (
    polygons: PolygonRings | undefined,
    ctx: z.RefinementCtx,
    { singleRing = false }: { singleRing?: boolean } = {},
): void => {
    if (singleRing && polygons && polygons.length !== 1) {
        ctx.addIssue({ code: "custom", path: ["polygons"], message: "Exactly one boundary is required." });
    }
    (polygons ?? []).forEach((ring, index) => {
        if (!isValidPolygonRing(ring)) {
            ctx.addIssue({
                code: "custom",
                path: ["polygons", index],
                message: "Each boundary needs at least 3 different points and must not cross itself.",
            });
        }
    });
};

type Boundary = NonNullable<ReturnType<typeof intersect>>;

const shoelaceArea = (ring: number[][]) =>
    Math.abs(ring.slice(1).reduce((sum, [lng, lat], index) => sum + ring[index][0] * lat - lng * ring[index][1], 0)) /
    2;

// Planar, like intersect's clipping; turf's spherical area() disagrees with it on thin shapes.
const planarArea = ({ geometry }: Boundary): number =>
    (geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates).reduce(
        (total, [outer, ...holes]) =>
            total + shoelaceArea(outer) - holes.reduce((sum, hole) => sum + shoelaceArea(hole), 0),
        0,
    );

const siteOverlapArea = (candidate: Boundary, site: { polygons: PolygonRings }): number => {
    const rings = site.polygons.map(toClosedRing);
    if (!rings.every((ring) => ring !== undefined)) throw new Error("ring has missing coordinates or under 3 points");
    const boundary = toMultiPolygon(rings);
    const overlap = boundary && intersect(featureCollection([candidate, boundary]));
    return overlap ? planarArea(overlap) : 0;
};

const measureOverlaps = <T extends { polygons: PolygonRings }>(candidate: Boundary, sites: readonly T[]) => {
    const measured: { site: T; overlapArea: number }[] = [];
    const failed: { site: T; error: unknown }[] = [];
    sites.forEach((site) => {
        try {
            measured.push({ site, overlapArea: siteOverlapArea(candidate, site) });
        } catch (error) {
            failed.push({ site, error });
        }
    });
    return { measured, failed };
};

export type SiteOverlaps<T> = { overlapping: T[]; failed: { site: T; error: unknown }[] };

export const findOverlappingSites = <T extends { polygons: PolygonRings }>(
    polygons: PolygonRings,
    others: readonly T[],
): SiteOverlaps<T> => {
    const candidate = toMultiPolygon(polygons.map(toClosedRing).filter((ring) => ring !== undefined));
    if (!candidate) return { overlapping: [], failed: [] };

    const { measured, failed } = measureOverlaps(candidate, others);
    return { overlapping: measured.filter(({ overlapArea }) => overlapArea > 0).map(({ site }) => site), failed };
};

// Float slack for a zone that shares edges with a site boundary.
const CONTAINMENT_RELATIVE_TOLERANCE = 1e-6;

export type SiteContainment<T> = { overlapping: T[]; containedBy?: T; failed: { site: T; error: unknown }[] };

export const findSiteContainment = <T extends { polygons: PolygonRings }>(
    polygons: PolygonRings,
    sites: readonly T[],
): SiteContainment<T> => {
    const candidate = toMultiPolygon(polygons.map(toClosedRing).filter((ring) => ring !== undefined));
    // Self-intersect so rings that overlap each other are not counted twice.
    const normalized = candidate && intersect(featureCollection([candidate, candidate]));
    if (!normalized) return { overlapping: [], failed: [] };
    const candidateArea = planarArea(normalized);

    const { measured, failed } = measureOverlaps(normalized, sites);
    const overlaps = measured.filter(({ overlapArea }) => overlapArea > candidateArea * CONTAINMENT_RELATIVE_TOLERANCE);
    const [only] = overlaps;
    const contained = overlaps.length === 1 && only.overlapArea >= candidateArea * (1 - CONTAINMENT_RELATIVE_TOLERANCE);
    return { overlapping: overlaps.map(({ site }) => site), ...(contained && { containedBy: only.site }), failed };
};

export const findSiteContainingPoint = <T extends { polygons: PolygonRings }>(
    sites: readonly T[],
    point: MapVertex,
): T | undefined => {
    if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return undefined;
    const lngLat = [point.lng as number, point.lat as number];
    return sites.find((site) =>
        site.polygons.some((vertices) => {
            const ring = toClosedRing(vertices);
            return ring !== undefined && pointInPolygon(lngLat, ring);
        }),
    );
};
